/**
 * Pack Découverte — Green Spa
 * ============================================================
 * Logique complète :
 *  • Étape 1 : sélection de packs (Corps / Visage / Complet)
 *  • Étape 2 : sélection à l'unité (1 variante max par produit)
 *  • Anti-abus : enforce "1 variante/produit" dans le panier Shopify
 *  • Compatible Shippingbo : le variant_id Shopify est toujours passé
 *    tel quel → Shippingbo récupère le SKU exact de la variante
 *
 * Remise :
 *  • Si la variante a un compare_at_price → prix barré = compare_at_price,
 *    prix affiché = price (déjà configuré dans Shopify Admin)
 *  • Sinon → on calcule : price * (1 - discountPercent / 100) pour l'affichage
 *    Note : le prix RÉEL au checkout = price du variant. Configurez vos prix
 *    ou utilisez un Automatic Discount Shopify (voir SETUP.md).
 * ============================================================
 */

(function () {
  'use strict';

  /* ── Helpers ──────────────────────────────────────────── */

  const fmt = (() => {
    const formatter = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    });
    // Shopify stocke les prix en centimes
    return (cents) => formatter.format(cents / 100);
  })();

  // Helpers extraits dans packdecouverteverlou-helpers.js (chargé en amont,
  // également utilisé par les tests unitaires Node).
  // Fallback safe si le fichier n'a pas été chargé (ex. cache navigateur).
  const helpers = (typeof window !== 'undefined' && window.PackDecouverteHelpers) || {
    computePrice: () => ({ original: 0, discounted: 0, savingsPct: 0 }),
    translateShopifyError: () => null,
    openThemeCartDrawer: () => false,
  };
  const { computePrice, translateShopifyError, openThemeCartDrawer } = helpers;

  /* ── PackDecouverte Class ─────────────────────────────── */

  class PackDecouverte {
    /**
     * @param {string} sectionId  - ID de la section Shopify
     */
    constructor(sectionId) {
      this.cfg = window.packDecouverte[sectionId];
      if (!this.cfg) return;

      this.sid = sectionId; // shorthand

      // ── State
      this.step = 1;
      // Map<productId:number, variantId:number>
      this.unitSelections = new Map();
      this.isLoading = false;

      // ── DOM root
      this.root = document.getElementById(`pd-root-${sectionId}`);
      if (!this.root) return;

      this.init();
    }

    init() {
      this._buildPacksStep();
      this._buildProductsStep();
      this._portalSummaryBar(); // doit être avant _bindEvents
      this._portalLoader();
      this._initPackPrices();
      this._bindEvents();
      // Pré-activer le code promo dès le chargement de la page.
      // Si l'utilisateur ajoute au panier puis va directement au checkout
      // sans passer par notre bouton, la remise est déjà active.
      this._preloadDiscountCodes();
      // Cadeaux offerts : réconcilier au chargement de la page (ajoute ou
      // retire les cadeaux selon la présence d'au moins un produit à l'unité).
      this._syncGifts();
    }

    /**
     * Déplace la barre récapitulative dans <body> pour que position:fixed
     * soit relatif au viewport et non à un ancêtre transformé (ex. Palo Alto
     * utilise CSS transform sur ses wrappers drawer, ce qui crée un nouveau
     * stacking context et rend position:fixed inefficace).
     */
    /** Déplace l'overlay de chargement dans <body> (même raison que summary bar). */
    _portalLoader() {
      const loader = document.getElementById(`pd-loading-${this.sid}`);
      if (!loader) return;
      const cs = getComputedStyle(this.root);
      ['--pd-primary', '--pd-border', '--pd-text-muted', '--pd-transition'].forEach((v) => {
        const val = cs.getPropertyValue(v).trim();
        if (val) loader.style.setProperty(v, val);
      });
      document.body.appendChild(loader);
    }

    _portalSummaryBar() {
      const bar = document.getElementById(`pd-summary-${this.sid}`);
      if (!bar) return;

      // Les variables CSS (--pd-primary, etc.) sont définies sur
      // .packdecouverteverlou-section. Une fois la barre hors du parent,
      // les var() ne se résolvent plus → bouton invisible.
      // On les copie directement sur la barre pour qu'elles restent disponibles.
      const cs = getComputedStyle(this.root);
      [
        '--pd-primary', '--pd-primary-hover', '--pd-primary-light',
        '--pd-accent', '--pd-accent-dark', '--pd-accent-light',
        '--pd-bg', '--pd-white', '--pd-text', '--pd-text-muted',
        '--pd-border', '--pd-border-focus', '--pd-radius', '--pd-radius-sm',
        '--pd-radius-xs', '--pd-shadow', '--pd-shadow-hover',
        '--pd-font-serif', '--pd-transition', '--pd-ease-spring',
      ].forEach((v) => {
        const val = cs.getPropertyValue(v).trim();
        if (val) bar.style.setProperty(v, val);
      });

      document.body.appendChild(bar);

      // Re-attacher le clic add-selection : la barre n'est plus dans this.root
      bar.addEventListener('click', (e) => {
        const actionEl = e.target.closest('[data-action]');
        if (actionEl?.dataset.action === 'add-selection') {
          this._addSelectionToCart();
        }
      });
    }

    /**
     * Active les codes promos en arrière-plan dès le chargement de la page.
     * Sans attente — le fetch est fire-and-forget (silencieux).
     */
    _preloadDiscountCodes() {
      // On ne précharge QUE le code packs (le plus courant, ou le seul configuré).
      //
      // Pourquoi pas les deux en parallèle ?
      // Le cookie Shopify _discount ne stocke qu'un seul code actif. Si on
      // appelle /discount/CODE1 et /discount/CODE2 en parallèle, l'ordre de
      // résolution est non-déterministe → race condition, un des deux codes
      // est silencieusement écrasé par l'autre.
      //
      // Solution : on précharge le code "packs" (cas le plus fréquent).
      // Le code "unite" sera appliqué explicitement via _applyDiscountCode()
      // juste avant l'ajout au panier — ce qui est toujours correct.
      const code = (this.cfg.discountCode || '').trim();
      if (!code) return;

      fetch(`/discount/${encodeURIComponent(code)}`, {
        method:      'GET',
        credentials: 'same-origin',
        redirect:    'follow',
      }).catch(() => {}); // silencieux — fire-and-forget
    }

    /* ======================================================
       BUILD — Étape 1 : cartes pack
       ====================================================== */

    _buildPacksStep() {
      const container = document.getElementById(`pd-packs-${this.sid}`);
      if (!container) return;

      // Vérification préalable : au moins un produit configuré
      if (!this.cfg.products.length) {
        container.innerHTML = `
          <div class="pd-empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <p>Aucun produit configuré.<br>Ajoutez des produits via <strong>l'éditeur de thème → Pack Découverte → + Ajouter un bloc</strong>.</p>
          </div>`;
        return;
      }

      const packs = [
        {
          type:     'corps',
          label:    this.cfg.packCorpsLabel,
          sub:      this.cfg.packCorpsSubtitle,
          badge:    null,
          featured: false,
          ctaLabel: this.cfg.packCorpsCta  || 'Choisir ce pack',
        },
        {
          type:     'visage',
          label:    this.cfg.packVisageLabel,
          sub:      this.cfg.packVisageSubtitle,
          badge:    this.cfg.packVisageBadge,
          featured: true,
          ctaLabel: this.cfg.packVisageCta || 'Choisir ce pack',
        },
        {
          type:     'complet',
          label:    this.cfg.packCompletLabel,
          sub:      this.cfg.packCompletSubtitle,
          badge:    this.cfg.packCompletBadge,
          featured: false,
          ctaLabel: this.cfg.packCompletCta || 'Choisir ce pack',
        },
      ];

      container.innerHTML = packs
        .map((pack) => this._renderPackCard(pack))
        .filter(Boolean)
        .join('');
    }

    /**
     * Retourne le label lisible d'un type de pack (utilisé comme propriété
     * visible dans le checkout Shopify : "Pack : Pack Corps").
     */
    _packLabel(packType) {
      const map = {
        corps:   this.cfg.packCorpsLabel   || 'Pack Corps',
        visage:  this.cfg.packVisageLabel  || 'Pack Visage',
        complet: this.cfg.packCompletLabel || 'Pack Complet',
      };
      return map[packType] || 'Pack Découverte';
    }

    /**
     * Détermine l'index de la variante recommandée par le marchand parmi
     * les variantes disponibles, en cherchant par titre exact (insensible
     * à la casse et aux espaces). Retourne 0 si aucune correspondance.
     */
    _recommendedVariantIndex(product, available) {
      const target = (product.recommendedVariant || '').trim().toLowerCase();
      if (!target) return 0;
      const idx = available.findIndex(
        (v) => (v.title || '').trim().toLowerCase() === target
      );
      return idx >= 0 ? idx : 0;
    }

    /**
     * Retourne les produits appartenant à un type de pack.
     * "complet" = tous les produits, dédupliqués par productId.
     * (Un marchand pourrait ajouter le même produit en 2 blocs différents.)
     */
    _getProductsForPack(packType) {
      let list;
      if (packType === 'complet') {
        // Complet = tous les produits SAUF ceux réservés à l'unité
        list = this.cfg.products.filter((p) => p.packType !== 'unite');
      } else {
        list = this.cfg.products.filter(
          (p) => p.packType === packType || p.packType === 'both'
        );
      }
      // Déduplique par productId
      const seen = new Set();
      return list.filter((p) => {
        if (seen.has(p.productId)) return false;
        seen.add(p.productId);
        return true;
      });
    }

    _renderPackCard(pack) {
      const products = this._getProductsForPack(pack.type);
      if (!products.length) return '';

      const badgeHtml = pack.badge
        ? `<div class="pd-pack-card__badge ${
            pack.type === 'complet' ? 'pd-pack-card__badge--gold' : ''
          }">${this._esc(pack.badge)}</div>`
        : '';

      // Image strip: first 3 product images (full-size for quality)
      const stripImgs = products.slice(0, 3)
        .map((p) => p.image
          ? `<img class="pd-pack-card__strip-img" src="${this._esc(p.image)}" alt="" loading="lazy">`
          : `<div class="pd-pack-card__strip-placeholder"></div>`
        ).join('');

      const count = products.length;
      const eyebrow = count === 1 ? '1 soin inclus' : `${count} soins inclus`;

      const productsHtml = products
        .map((p) => this._renderPackProductItem(p, pack.type))
        .join('');

      return `
        <article class="pd-pack-card${pack.featured ? ' pd-pack-card--featured' : ''}"
                 data-pack="${pack.type}">
          ${badgeHtml}
          <div class="pd-pack-card__strip">${stripImgs}</div>
          <div class="pd-pack-card__body">
            <div>
              <h3 class="pd-pack-card__title">${this._esc(pack.label)}</h3>
              <p class="pd-pack-card__subtitle">${this._esc(pack.sub)}</p>
            </div>
            <ul class="pd-pack-product-list">${productsHtml}</ul>
            <div class="pd-pack-price" id="pd-pack-price-${this.sid}-${pack.type}">
              <span class="pd-pack-price__original"></span>
              <span class="pd-pack-price__discounted"></span>
              <span class="pd-pack-price__savings"></span>
            </div>
            <button class="pd-btn pd-btn--primary"
                    data-action="add-pack"
                    data-pack="${pack.type}"
                    aria-label="${this._esc(pack.label)} — ${this._esc(pack.ctaLabel)}">
              ${this._esc(pack.ctaLabel)}
            </button>
          </div>
        </article>
      `;
    }

    _renderPackProductItem(product, packContext) {
      const available = product.variants.filter((v) => v.available);
      if (!available.length) return '';

      // Index de la variante recommandée (0 si non spécifiée ou introuvable)
      const recommendedIdx = this._recommendedVariantIndex(product, available);

      const imgHtml = product.imageSm
        ? `<img class="pd-pack-product-item__img"
                src="${this._esc(product.imageSm)}"
                alt="${this._esc(product.title)}"
                loading="lazy">`
        : `<div class="pd-pack-product-item__img-placeholder" aria-hidden="true">
             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
           </div>`;

      let variantHtml;
      // Quand lockVariants est actif, on ignore le select et on affiche
      // uniquement la variante recommandée (ou la première disponible).
      if (available.length > 1 && !this.cfg.lockVariants) {
        // ⚠️ Pas de retour à la ligne dans le contenu de <option> sinon le
        // texte du dropdown contient des espaces parasites visibles.
        const options = available
          .map(
            (v, i) =>
              `<option value="${v.id}" data-price="${v.price}" data-compare="${v.compareAtPrice}"${i === recommendedIdx ? ' selected' : ''}>${this._esc(v.title)}</option>`
          )
          .join('');
        variantHtml = `
          <select class="pd-pack-product-item__select"
                  data-product-id="${product.productId}"
                  data-pack-context="${packContext}"
                  aria-label="Choisir la taille de ${this._esc(product.title)}">${options}</select>`;
      } else {
        // Variante unique OU variantes verrouillées → variante recommandée fixe.
        // On n'affiche PAS le label de variante dans les pack cards : le produit
        // est un bundle Shopify (ex "Pack Découverte Corps") dont la variante
        // n'a pas de sens à montrer au client (évite "12 format revente").
        const v = available[recommendedIdx] || available[0];
        variantHtml = `
          <input type="hidden"
                 class="pd-pack-product-item__select"
                 data-product-id="${product.productId}"
                 data-pack-context="${packContext}"
                 value="${v.id}"
                 data-price="${v.price}"
                 data-compare="${v.compareAtPrice}">`;
      }

      return `
        <li class="pd-pack-product-item" data-product-id="${product.productId}">
          ${imgHtml}
          <div class="pd-pack-product-item__info">
            <div class="pd-pack-product-item__name">${this._esc(product.title)}</div>
            ${variantHtml}
          </div>
        </li>
      `;
    }

    /* ======================================================
       BUILD — Étape 2 : grille produits
       ====================================================== */

    _buildProductsStep() {
      const container   = document.getElementById(`pd-products-${this.sid}`);
      const navEl       = document.getElementById(`pd-cat-nav-${this.sid}`);
      if (!container) return;

      const uniteProducts = this.cfg.products.filter((p) => p.packType === 'unite');

      if (!uniteProducts.length) {
        container.innerHTML = `
          <div class="pd-empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <p>Tous les produits sont actuellement épuisés.<br>Revenez bientôt ou <a href="/collections/all" style="color:var(--pd-primary)">parcourez notre catalogue</a>.</p>
          </div>`;
        return;
      }

      // Group by category (preserve insertion order)
      const groups = new Map();
      for (const p of uniteProducts) {
        const cat = (p.category || '').trim() || '';
        if (!groups.has(cat)) groups.set(cat, []);
        groups.get(cat).push(p);
      }

      // Use categories only if at least one product has a non-empty category
      const hasCategories = [...groups.keys()].some((k) => k !== '');

      // Séparer catégories corps vs visage (les catégories "visage" commencent par "visage")
      const corpsCats  = new Map([...groups].filter(([k]) => k && !k.toLowerCase().startsWith('visage')));
      const visageCats = new Map([...groups].filter(([k]) => k &&  k.toLowerCase().startsWith('visage')));
      const otherCats  = new Map([...groups].filter(([k]) => !k));
      const hasGroupSplit = hasCategories && corpsCats.size > 0 && visageCats.size > 0;

      // Helper : retire le préfixe "Visage " des noms d'affichage
      const displayName = (cat) => cat.replace(/^visage\s*/i, '') || cat;

      let html = '';

      if (hasCategories) {
        container.classList.add('pd-products-grid--categorized');
        const renderGroup = (groupMap) => {
          for (const [cat, products] of groupMap) {
            const catId = `pd-cat-${this.sid}-${(cat || 'autres').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
            const cards = products.map((p) => this._renderProductCard(p)).filter(Boolean);
            if (!cards.length) continue;
            const title = displayName(cat || 'Autres');
            html += `
              <section class="pd-cat-section" id="${catId}" data-category="${this._esc(cat)}">
                <h3 class="pd-cat-title">${this._esc(title)}</h3>
                <div class="pd-cat-grid">${cards.join('')}</div>
              </section>`;
          }
        };

        if (hasGroupSplit) {
          html += `<div class="pd-cat-group" data-group="corps">`;
          renderGroup(corpsCats);
          html += `</div>`;
          html += `<div class="pd-cat-group" data-group="visage">`;
          renderGroup(visageCats);
          html += `</div>`;
        } else {
          renderGroup(corpsCats.size ? corpsCats : visageCats.size ? visageCats : groups);
        }
        renderGroup(otherCats);
      } else {
        container.classList.remove('pd-products-grid--categorized');
        const cards = uniteProducts.map((p) => this._renderProductCard(p)).filter(Boolean);
        html = cards.join('');
      }

      container.innerHTML = html || '';

      // Build nav
      if (navEl && hasCategories) {
        let navHtml = '';
        let firstBtn = true;

        const buildNavRow = (label, groupMap) => {
          let rowHtml = `<div class="pd-cat-nav__row">`;
          if (label) rowHtml += `<span class="pd-cat-nav__group-label">${label}</span>`;
          for (const [cat] of groupMap) {
            if (!cat) continue;
            const catId = `pd-cat-${this.sid}-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
            const lbl   = displayName(cat);
            rowHtml += `<button class="pd-cat-nav__item${firstBtn ? ' pd-cat-nav__item--active' : ''}"
                                data-cat-target="${catId}"
                                type="button">${this._esc(lbl)}</button>`;
            firstBtn = false;
          }
          rowHtml += `</div>`;
          return rowHtml;
        };

        if (hasGroupSplit) {
          navHtml += buildNavRow('Corps', corpsCats);
          navHtml += buildNavRow('Visage', visageCats);
        } else {
          navHtml += buildNavRow('', groups);
        }

        navEl.innerHTML = navHtml;
        navEl.style.display = 'flex';
        this._bindCatNav(navEl);
      } else if (navEl) {
        navEl.style.display = 'none';
      }
    }

    _bindCatNav(navEl) {
      let obs = null;

      const themeHeader = document.querySelector(
        '.site-header, header.header, #shopify-section-header, [data-section-type="header"]'
      );

      // Le header du thème change de hauteur au scroll (logo qui rétrécit,
      // header qui se masque) : on colle la nav sous son bord bas ACTUEL,
      // sinon un vide apparaît entre le menu et la nav.
      const _syncTop = () => {
        const bottom = themeHeader
          ? Math.max(0, Math.round(themeHeader.getBoundingClientRect().bottom))
          : 0;
        navEl.style.top = bottom + 'px';
      };

      // Calcule l'offset total (header thème + nav) et l'applique partout
      const _applyOffsets = () => {
        const headerH = themeHeader ? themeHeader.getBoundingClientRect().height : 0;
        _syncTop();

        const navH    = navEl.getBoundingClientRect().height;
        const totalH  = headerH + navH;

        // scroll-margin-top sur toutes les sections de catégorie
        document.querySelectorAll(`[id^="pd-cat-${this.sid}-"]`).forEach((s) => {
          s.style.scrollMarginTop = (totalH + 8) + 'px';
        });

        // Recrée l'IntersectionObserver avec le bon rootMargin
        if (obs) obs.disconnect();
        const sections = document.querySelectorAll(`[id^="pd-cat-${this.sid}-"]`);
        if (!sections.length) return;
        obs = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              navEl.querySelectorAll('.pd-cat-nav__item').forEach((b) => {
                b.classList.toggle('pd-cat-nav__item--active', b.dataset.catTarget === entry.target.id);
              });
            }
          }
        }, { threshold: 0.15, rootMargin: `-${totalH + 4}px 0px -55% 0px` });
        sections.forEach((s) => obs.observe(s));
      };

      // Applique après un tick (DOM stable) et sur resize
      requestAnimationFrame(_applyOffsets);
      window.addEventListener('resize', _applyOffsets, { passive: true });

      // Suivi du bord bas du header pendant le scroll (throttle rAF)
      let syncQueued = false;
      const _queueSync = () => {
        if (syncQueued) return;
        syncQueued = true;
        requestAnimationFrame(() => {
          syncQueued = false;
          _syncTop();
        });
      };
      window.addEventListener('scroll', _queueSync, { passive: true });
      if (themeHeader && 'ResizeObserver' in window) {
        new ResizeObserver(_queueSync).observe(themeHeader);
      }

      navEl.addEventListener('click', (e) => {
        const btn = e.target.closest('.pd-cat-nav__item');
        if (!btn) return;
        const targetId = btn.dataset.catTarget;
        const target   = document.getElementById(targetId);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        navEl.querySelectorAll('.pd-cat-nav__item').forEach((b) => b.classList.remove('pd-cat-nav__item--active'));
        btn.classList.add('pd-cat-nav__item--active');
      });
    }

    _renderProductCard(product) {
      const allVariants    = product.variants;
      if (!allVariants.length) return '';

      // Grille à l'unité = petits formats "tester" uniquement.
      // Si le produit n'a plus de petit format disponible mais qu'un grand
      // format (1000 ml / 1 kg / 5000 ml) l'est, on ne l'affiche pas du tout.
      const isLargeFormat = (v) => {
        const t = (v.title || '').toLowerCase().replace(/\s+/g, '');
        return t.includes('1000ml') || t.includes('5000ml') || t.includes('1000g') || t.includes('1kg');
      };
      const smallAvailable = allVariants.some((v) => v.available && !isLargeFormat(v));
      const largeAvailable = allVariants.some((v) => v.available && isLargeFormat(v));
      // Exception : si le bloc désigne explicitement un grand format comme
      // variante recommandée (ex. huiles 1 L / 5 L à -20 %), on l'affiche.
      const recTarget = (product.recommendedVariant || '').trim().toLowerCase();
      const explicitLarge = !!recTarget && allVariants.some(
        (v) => isLargeFormat(v) && (v.title || '').trim().toLowerCase() === recTarget
      );
      if (!smallAvailable && largeAvailable && !explicitLarge) return '';

      // Variante recommandée = celle configurée dans le bloc, sinon le premier
      // petit format disponible (jamais un grand format), sinon la première.
      const recommendedIdx = this._recommendedVariantIndex(product, allVariants);
      const primaryVariant =
        allVariants[recommendedIdx] ||
        allVariants.find((v) => v.available && !isLargeFormat(v)) ||
        allVariants[0];

      const imgHtml = product.image
        ? `<img class="pd-product-card__img"
                src="${this._esc(product.image)}"
                alt="${this._esc(product.title)}"
                loading="lazy">`
        : `<div class="pd-product-card__img" style="background:var(--pd-bg)"></div>`;

      // ── Carte OOS : variante recommandée en rupture ──────────
      // Le produit est entier indisponible dans ce contexte (pas de fallback).
      if (!primaryVariant.available) {
        const oosLabel = primaryVariant.title !== 'Default Title'
          ? this._esc(primaryVariant.title)
          : '';
        return `
          <div class="pd-product-card pd-product-card--oos"
               id="pd-product-${this.sid}-${product.productId}"
               data-product-id="${product.productId}"
               aria-disabled="true"
               aria-label="${this._esc(product.title)} — rupture de stock">
            <div class="pd-product-card__img-wrap">
              ${imgHtml}
            </div>
            <div class="pd-product-card__body">
              <h4 class="pd-product-card__name">${this._esc(product.title)}</h4>
              ${oosLabel ? `<span class="pd-product-card__oos-pill">${oosLabel}</span>` : ''}
              <p class="pd-product-card__oos-label">Rupture de stock</p>
            </div>
          </div>`;
      }

      // ── Carte disponible ─────────────────────────────────────
      const available = allVariants.filter((v) => v.available);
      if (!available.length) return '';

      const hasNamedVariants = allVariants.some((v) => v.title !== 'Default Title');

      let variantHtml = '';
      if (!this.cfg.lockVariants && hasNamedVariants) {
        // Pill unique pour la variante recommandée (disponible)
        const v     = primaryVariant;
        const label = this._esc(v.title !== 'Default Title' ? v.title : '');
        variantHtml = `<div class="pd-variant-pills" data-product-id="${product.productId}">
          <button class="pd-variant-pill pd-variant-pill--active"
                  data-action="select-variant"
                  data-variant-id="${v.id}"
                  data-price="${v.price}"
                  data-compare="${v.compareAtPrice}"
                  data-product-id="${product.productId}"
                  type="button"
                  aria-pressed="true">
            ${label}
          </button>
        </div>`;
      } else if (this.cfg.lockVariants && hasNamedVariants) {
        if (primaryVariant.title !== 'Default Title') {
          variantHtml = `<p class="pd-product-card__variant-fixed">${this._esc(primaryVariant.title)}</p>`;
        }
      }

      return `
        <div class="pd-product-card"
             id="pd-product-${this.sid}-${product.productId}"
             data-product-id="${product.productId}"
             data-action="toggle-product"
             role="button"
             tabindex="0"
             aria-pressed="false"
             aria-label="Sélectionner ${this._esc(product.title)}">
          <div class="pd-product-card__img-wrap">
            ${imgHtml}
            <div class="pd-product-card__check" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
          </div>
          <div class="pd-product-card__body">
            <h4 class="pd-product-card__name">${this._esc(product.title)}</h4>
            ${product.description ? `<p class="pd-product-card__desc">${this._esc(product.description).replace(/\n/g, '<br>')}</p>` : ''}
            ${variantHtml}
            <div class="pd-product-card__price"
                 id="pd-product-price-${this.sid}-${product.productId}">
              <span class="pd-product-card__price-discounted"></span>
              <span class="pd-product-card__price-original"></span>
            </div>
          </div>
        </div>
      `;
    }

    /* ======================================================
       PRICES — Initialisation
       ====================================================== */

    _initPackPrices() {
      // Les prix des cartes pack dépendent du DOM, donc on les calcule
      // après que buildPacksStep a été appelé.
      ['corps', 'visage', 'complet'].forEach((t) => this._recalcPackPrice(t));

      // Prix produits étape 2 : seulement les produits "unite"
      // Utilise discountPercentUnite comme taux par défaut (50%)
      // sauf si le bloc a une remise spécifique.
      this.cfg.products
        .filter((p) => p.packType === 'unite')
        .forEach((prod) => {
          const allVariants = prod.variants;
          const available   = allVariants.filter((v) => v.available);
          if (!available.length) return;
          const recIdx  = this._recommendedVariantIndex(prod, allVariants);
          const idx     = allVariants[recIdx]?.available ? recIdx : allVariants.findIndex((v) => v.available);
          const recommended = allVariants[idx] || available[0];
          // Remise spécifique du bloc > remise à l'unité > remise globale
          const discount =
            prod.discountPercent ||
            this.cfg.discountPercentUnite ||
            this.cfg.discountPercent;
          this._updateProductCardPrice(prod.productId, recommended, discount);
        });
    }

    _recalcPackPrice(packType) {
      const card = this.root.querySelector(`[data-pack="${packType}"]`);
      const priceEl = document.getElementById(`pd-pack-price-${this.sid}-${packType}`);
      if (!card || !priceEl) return;

      const products = this._getProductsForPack(packType);
      let totalOriginal = 0;
      let totalDiscounted = 0;

      products.forEach((prod) => {
        const sel = card.querySelector(
          `.pd-pack-product-item__select[data-product-id="${prod.productId}"]`
        );
        if (!sel) return;

        // Récupère price/compare depuis l'option active (select)
        // ou directement depuis l'input hidden (1 seule variante).
        let rawPrice, rawCompare;
        if (sel.tagName === 'SELECT') {
          const opt = sel.options[sel.selectedIndex];
          rawPrice   = opt?.dataset.price;
          rawCompare = opt?.dataset.compare;
        } else {
          rawPrice   = sel.dataset.price;
          rawCompare = sel.dataset.compare;
        }

        const { original, discounted } = computePrice(
          rawPrice,
          rawCompare,
          prod.discountPercent
        );
        totalOriginal   += original;
        totalDiscounted += discounted;
      });

      // Aucun produit valide → on n'affiche rien
      if (totalOriginal === 0) {
        priceEl.querySelector('.pd-pack-price__original').textContent   = '';
        priceEl.querySelector('.pd-pack-price__discounted').textContent = '';
        priceEl.querySelector('.pd-pack-price__savings').textContent    = '';
        return;
      }

      const savingsPct = Math.max(
        0,
        Math.round((1 - totalDiscounted / totalOriginal) * 100)
      );

      priceEl.querySelector('.pd-pack-price__original').textContent   = fmt(totalOriginal);
      priceEl.querySelector('.pd-pack-price__discounted').textContent = fmt(totalDiscounted);

      // Masque le badge si la remise est nulle (sinon "-0%" est inesthétique)
      const savingsEl = priceEl.querySelector('.pd-pack-price__savings');
      if (savingsPct > 0) {
        savingsEl.textContent = `-${savingsPct}%`;
        savingsEl.hidden = false;
      } else {
        savingsEl.textContent = '';
        savingsEl.hidden = true;
      }
    }

    _updateProductCardPrice(productId, variant, discountPct) {
      const priceEl = document.getElementById(
        `pd-product-price-${this.sid}-${productId}`
      );
      if (!priceEl) return;

      const rawPrice   = variant.price   ?? variant.dataset?.price;
      const rawCompare = variant.compareAtPrice ?? variant.dataset?.compare;
      // Fallback : si discountPct non fourni, on cherche le produit dans cfg
      // pour utiliser le bon taux (unite=50%, packs=80%).
      const pct = discountPct != null
        ? discountPct
        : this._effectiveDiscount(
            this.cfg.products.find((p) => p.productId === productId)
          );

      const { original, discounted, savingsPct } = computePrice(
        rawPrice,
        rawCompare,
        pct
      );

      priceEl.querySelector('.pd-product-card__price-discounted').textContent = fmt(discounted);
      priceEl.querySelector('.pd-product-card__price-original').textContent   = fmt(original);

      // Badge -X% : créé à la volée et masqué si la remise est nulle
      let savingsEl = priceEl.querySelector('.pd-product-card__price-savings');
      if (!savingsEl) {
        savingsEl = document.createElement('span');
        savingsEl.className = 'pd-product-card__price-savings';
        priceEl.appendChild(savingsEl);
      }
      if (savingsPct > 0) {
        savingsEl.textContent = `-${savingsPct}%`;
        savingsEl.hidden = false;
      } else {
        savingsEl.textContent = '';
        savingsEl.hidden = true;
      }

      // Si le compare est égal/inférieur au prix, on n'affiche pas le prix barré
      const originalEl = priceEl.querySelector('.pd-product-card__price-original');
      if (originalEl) originalEl.hidden = (original === discounted);
    }

    /* ======================================================
       EVENTS
       ====================================================== */

    _bindEvents() {
      // Clicks délégués
      this.root.addEventListener('click', (e) => {
        const actionEl = e.target.closest('[data-action]');
        if (!actionEl) return;

        switch (actionEl.dataset.action) {
          case 'goto-step-2':
            this._goToStep(2);
            break;

          case 'goto-step-1':
            this._goToStep(1);
            break;

          case 'add-pack': {
            const packType = actionEl.closest('[data-pack]')?.dataset.pack;
            if (packType) this._addPackToCart(packType);
            break;
          }

          case 'toggle-product': {
            // Ne pas déclencher si le clic est sur une pill de variante
            if (e.target.closest('.pd-variant-pills')) return;
            const card = actionEl.closest('[data-product-id]');
            if (card) this._toggleProduct(parseInt(card.dataset.productId, 10));
            break;
          }

          case 'select-variant': {
            e.stopPropagation(); // Ne pas déclencher toggle-product
            const pill = e.target.closest('.pd-variant-pill');
            if (pill) this._onUnitVariantChange(pill);
            break;
          }

          case 'add-selection':
            this._addSelectionToCart();
            break;

          case 'reset':
            this._reset();
            break;

          case 'close-toast':
            this._hideToast();
            break;
        }
      });

      // Keyboard accessibility pour les cartes produit
      this.root.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.dataset.action === 'toggle-product') {
          e.preventDefault();
          this._toggleProduct(parseInt(e.target.dataset.productId, 10));
        }
      });

      // Changement de variante dans les cartes pack (select)
      this.root.addEventListener('change', (e) => {
        if (e.target.classList.contains('pd-pack-product-item__select')) {
          const packContext = e.target.dataset.packContext;
          if (packContext) this._recalcPackPrice(packContext);
          // Pack complet = recalc aussi
          if (packContext !== 'complet') this._recalcPackPrice('complet');
        }
      });
    }

    /* ======================================================
       NAVIGATION
       ====================================================== */

    /**
     * Les deux parties sont affichées sur la même page : on se contente
     * de faire défiler jusqu'à la partie demandée (1 = packs, 2 = à l'unité).
     */
    _goToStep(step) {
      this.step = step;

      const target = step === 2
        ? this.root.querySelector('.pd-part--unite')
        : this.root;
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ======================================================
       ÉTAPE 2 — Sélection variantes (pills)
       ====================================================== */

    _onUnitVariantChange(pill) {
      const productId = parseInt(pill.dataset.productId, 10);
      const variantId = parseInt(pill.dataset.variantId, 10);
      const price     = parseInt(pill.dataset.price, 10);
      const compare   = parseInt(pill.dataset.compare || 0, 10);

      // Met à jour les pills
      const container = pill.closest('.pd-variant-pills');
      if (container) {
        container.querySelectorAll('.pd-variant-pill').forEach((p) => {
          p.classList.remove('pd-variant-pill--active');
          p.setAttribute('aria-pressed', 'false');
        });
        pill.classList.add('pd-variant-pill--active');
        pill.setAttribute('aria-pressed', 'true');
      }

      // Si le produit est déjà sélectionné, met à jour la variante
      if (this.unitSelections.has(productId)) {
        this.unitSelections.set(productId, variantId);
        this._updateSelectionSummary();
      }

      // Met à jour le prix affiché avec le bon taux (unite=50%, packs=80%)
      const prod = this.cfg.products.find((p) => p.productId === productId);
      this._updateProductCardPrice(
        productId,
        { price, compareAtPrice: compare },
        this._effectiveDiscount(prod)
      );
    }

    /* ======================================================
       ÉTAPE 2 — Toggle sélection produit
       ====================================================== */

    _toggleProduct(productId) {
      if (this.unitSelections.has(productId)) {
        this.unitSelections.delete(productId);
      } else {
        const variantId = this._getActiveVariantId(productId);
        if (!variantId) return;
        this.unitSelections.set(productId, variantId);
      }

      const card = document.getElementById(`pd-product-${this.sid}-${productId}`);
      if (card) {
        const selected = this.unitSelections.has(productId);
        card.classList.toggle('pd-product-card--selected', selected);
        card.setAttribute('aria-pressed', String(selected));
      }

      this._updateSelectionSummary();
    }

    /**
     * Retourne l'ID de la variante actuellement sélectionnée (pill active
     * ou variante recommandée / première disponible si produit sans choix).
     */
    _getActiveVariantId(productId) {
      const prod = this.cfg.products.find((p) => p.productId === productId);

      // Variantes verrouillées → toujours la variante recommandée par le marchand
      if (this.cfg.lockVariants && prod) {
        const available = prod.variants.filter((v) => v.available);
        const idx = this._recommendedVariantIndex(prod, available);
        return available[idx]?.id ?? null;
      }

      const card = document.getElementById(`pd-product-${this.sid}-${productId}`);
      if (card) {
        const activePill = card.querySelector('.pd-variant-pill--active');
        if (activePill) {
          if (activePill.classList.contains('pd-variant-pill--unavailable')) return null;
          return parseInt(activePill.dataset.variantId, 10);
        }
      }
      // Fallback : première variante disponible
      return prod?.variants.find((v) => v.available)?.id ?? null;
    }

    _updateSelectionSummary() {
      const count = this.unitSelections.size;
      const total = this._calcSelectionTotal();

      const countEl  = document.getElementById(`pd-count-${this.sid}`);
      const totalEl  = document.getElementById(`pd-total-${this.sid}`);
      const addBtn   = document.getElementById(`pd-add-btn-${this.sid}`);

      if (countEl) {
        countEl.textContent = count === 0
          ? '0 produit sélectionné'
          : `${count} produit${count > 1 ? 's' : ''} sélectionné${count > 1 ? 's' : ''}`;
      }
      if (totalEl)  totalEl.textContent = fmt(total);
      if (addBtn)   addBtn.disabled = count === 0;

      // Barre récap : visible seulement quand au moins un produit est sélectionné
      const bar = document.getElementById(`pd-summary-${this.sid}`);
      if (bar) bar.classList.toggle('pd-summary-bar--visible', count > 0);

      // Avertissement si dépassement du seuil Shopify
      this._toggleWarning(count > this.cfg.maxItemsWarning);
    }

    _calcSelectionTotal() {
      let total = 0;
      this.unitSelections.forEach((variantId, productId) => {
        const prod    = this.cfg.products.find((p) => p.productId === productId);
        const variant = prod?.variants.find((v) => v.id === variantId);
        if (!variant) return;

        const { discounted } = computePrice(
          variant.price,
          variant.compareAtPrice,
          this._effectiveDiscount(prod) // bug fix: discountPercentUnite pour unite
        );
        total += discounted;
      });
      return total;
    }

    _toggleWarning(show) {
      let warn = this.root.querySelector('.pd-warning');
      if (!warn) {
        warn = document.createElement('div');
        warn.className = 'pd-warning';
        warn.textContent = `⚠️ Attention : au-delà de ${this.cfg.maxItemsWarning} articles, les remises automatiques Shopify peuvent ne pas s'appliquer à tous les produits.`;
        const bar = document.getElementById(`pd-summary-${this.sid}`);
        bar?.parentNode?.insertBefore(warn, bar);
      }
      warn.classList.toggle('pd-warning--visible', show);
    }

    /* ======================================================
       CART — Ajouter un pack
       ====================================================== */

    async _addPackToCart(packType) {
      const card = this.root.querySelector(`[data-pack="${packType}"]`);
      if (!card) return;

      const products = this._getProductsForPack(packType);
      const items = [];

      for (const prod of products) {
        const sel = card.querySelector(
          `.pd-pack-product-item__select[data-product-id="${prod.productId}"]`
        );
        if (!sel) continue;

        let variantId;
        if (sel.tagName === 'SELECT') {
          variantId = parseInt(sel.value, 10);
        } else {
          variantId = parseInt(sel.value || sel.dataset.variantId, 10);
        }
        if (!variantId) continue;

        items.push({
          id: variantId,
          quantity: 1,
          properties: {
            // Propriété VISIBLE (sans _) : affichée par Shopify dans le checkout,
            // la confirmation de commande et les emails. Ex : "Pack : Pack Corps"
            Pack:                  this._packLabel(packType),
            // Propriétés internes (underscore = masquées dans le checkout)
            _pack_type:            packType,
            _packdecouverteverlou: 'true',
            _source:               'packdecouverteverlou',
          },
        });
      }

      if (!items.length) {
        this._showToast(
          'Tous les produits de ce pack sont actuellement épuisés. Veuillez recharger la page.'
        );
        return;
      }
      await this._performCartAdd(items, {
        replaceExistingPack: !!this.cfg.replaceExistingPack,
        eventName: `add_pack_${packType}`,
      });
    }

    /* ======================================================
       CART — Ajouter la sélection à l'unité
       ====================================================== */

    async _addSelectionToCart() {
      if (!this.unitSelections.size) return;

      const items = [];
      this.unitSelections.forEach((variantId, productId) => {
        items.push({
          id: variantId,
          quantity: 1,
          properties: {
            Pack:                  "Sélection à l'unité",
            _pack_type:            'unite',
            _packdecouverteverlou: 'true',
            _source:               'packdecouverteverlou',
            _product_id:           String(productId),
          },
        });
      });

      await this._performCartAdd(items, {
        replaceExistingPack: !!this.cfg.replaceExistingPack,
        eventName: 'add_unit_selection',
        // Code promo dédié à l'unité, sinon fallback sur le code packs
        discountCodeOverride: this.cfg.discountCodeUnite || null,
      });
    }

    /* ======================================================
       CART — Core (avec anti-abus)
       ====================================================== */

    /**
     * Ajoute des articles au panier Shopify en s'assurant qu'il n'y a
     * jamais deux variantes du même produit simultanément (anti-abus).
     *
     * Compatibilité Shippingbo :
     *   - On passe toujours le variant_id Shopify exact
     *   - Shippingbo lit le SKU directement depuis la variante Shopify
     *   - Les propriétés de ligne (_pack_type etc.) sont visibles dans
     *     Shippingbo sous "Notes de commande" selon la configuration
     *
     * @param {{ id: number, quantity: number, properties: object }[]} items
     * @param {object} [opts]
     * @param {boolean} [opts.replaceExistingPack] - vide d'abord les anciens
     *   articles _packdecouverteverlou du panier (réconciliation forte)
     * @param {string}  [opts.eventName] - nom de l'événement analytics
     */
    async _performCartAdd(items, opts = {}) {
      if (this.isLoading) return;
      this._setLoading(true);

      try {
        // 1. Récupérer le panier actuel
        const cart = await this._fetchCart();

        // 2. Collecter les line-item keys à supprimer
        //    ⚠️  /cart/update.js n'accepte que des variant IDs comme clés.
        //    Pour cibler des line items précis (par leur key hash Shopify),
        //    on utilise /cart/change.js qui accepte directement la key.
        const keysToRemove = new Set();

        // 2a. Réconciliation forte : si activée, on retire d'abord TOUS les
        //     articles précédemment ajoutés via le pack-découverte. Évite
        //     l'accumulation si l'utilisateur revient et change d'avis.
        if (opts.replaceExistingPack) {
          cart.items.forEach((ci) => {
            const isPackItem =
              ci.properties && ci.properties._packdecouverteverlou === 'true';
            if (isPackItem) keysToRemove.add(ci.key);
          });
        }

        // 2b. Anti-abus "1 variante max par produit" : retirer toute autre
        //     variante du même produit déjà présente dans le panier.
        for (const newItem of items) {
          const productId = this._productIdForVariant(newItem.id);
          if (productId == null) continue;

          cart.items.forEach((ci) => {
            if (
              this._productIdForVariant(ci.variant_id) === productId &&
              ci.variant_id !== newItem.id
            ) {
              keysToRemove.add(ci.key);
            }
          });
        }

        // 3. Appliquer les suppressions si nécessaire
        if (keysToRemove.size > 0) {
          await this._cartRemoveItems(keysToRemove);
        }

        // 4. Ajouter les nouveaux articles
        const resp = await this._fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          // Tente de traduire le message d'erreur Shopify en français
          const friendly = translateShopifyError(
            errBody.description || errBody.message,
            resp.status
          );
          throw new Error(
            friendly ||
              errBody.description ||
              `Erreur ${resp.status} lors de l'ajout au panier`
          );
        }

        // 5. Marquer le panier comme "passé par le module Pack Découverte"
        //    → Shopify transfère les attributs panier en note_attributes sur la commande
        //    → Shopify Flow lit cet attribut pour taguer le client (valeur configurable via customer_tag dans les réglages de section)
        await this._fetchWithTimeout('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { '_pack_decouverte': this.cfg.customerTag || 'oui' } }),
        }).catch(() => { /* non-bloquant */ });

        // 6. Appliquer le code promo Shopify si configuré
        //    Sans ça, le panier/checkout affichera le prix NORMAL de la variante
        //    (Shopify ne connaît pas la remise UI calculée côté client).
        await this._applyDiscountCode(opts.discountCodeOverride);

        // 6bis. Cadeaux offerts : ajoute/retire les cadeaux selon qu'il reste
        //       au moins un produit à l'unité dans le panier (avant le post-add
        //       pour que le drawer/checkout les inclue déjà).
        await this._syncGifts();

        // 6. Mettre à jour le compteur du header si le thème l'expose
        this._refreshCartCount();

        // 7. Analytics : émission des événements pour GA4/GTM/dataLayer
        this._dispatchAnalytics(opts.eventName || 'add_to_cart', { items });

        // 8. Action post-ajout : drawer / redirection / écran succès
        //    On passe le code effectivement utilisé pour garantir la cohérence
        //    de l'URL /discount/CODE?redirect=/cart en mode redirect.
        const effectiveCode = (opts.discountCodeOverride || this.cfg.discountCode || '').trim();
        this._handlePostAddAction(effectiveCode);

      } catch (err) {
        console.error('[PackDecouverte] Erreur panier :', err);
        // AbortError = timeout réseau → message explicite au lieu de "The operation was aborted."
        const msg = err.name === 'AbortError'
          ? "La requête a pris trop de temps. Vérifiez votre connexion et réessayez."
          : (err.message || "Une erreur est survenue lors de l'ajout au panier. Veuillez réessayer.");
        this._showToast(msg);
      } finally {
        this._setLoading(false);
      }
    }

    /**
     * Applique automatiquement un code promo Shopify au panier.
     *
     * Pourquoi : la remise affichée sur la page est calculée côté client
     * (price * (1 - discount/100) ou compare_at_price). Au checkout, Shopify
     * utilise le prix RÉEL de la variante. Sans code promo ou compare_at_price,
     * le client paie le prix plein.
     *
     * Comment : l'URL /discount/CODE pose un cookie Shopify qui applique
     * le code à la session en cours. Fonctionne sur tous les thèmes.
     * On utilise `redirect: 'follow'` car Shopify répond avec une 302.
     *
     * Si le marchand a plutôt configuré compare_at_price ou une remise
     * automatique Shopify, il laisse la setting vide et cette méthode
     * devient un no-op.
     */
    async _applyDiscountCode(overrideCode) {
      // overrideCode (ex: discountCodeUnite) > code packs global
      const code = (overrideCode || this.cfg.discountCode || '').trim();
      if (!code) return;

      try {
        await this._fetchWithTimeout(
          `/discount/${encodeURIComponent(code)}`,
          {
            method: 'GET',
            credentials: 'same-origin',
            redirect: 'follow',
            headers: { Accept: 'text/html' },
          },
          8000
        );
      } catch (err) {
        // Silencieux : on ne bloque pas l'ajout au panier si l'activation
        // échoue. Le client verra juste le prix normal — mieux vaut qu'il
        // puisse checkout qu'une erreur bloquante.
        console.warn(
          '[PackDecouverte] Activation du code promo échouée :',
          err
        );
      }
    }

    /**
     * Action après ajout au panier réussi.
     * Comportement contrôlé par la setting `post_add_action` :
     *   - 'success' (défaut) → écran de succès dans la section
     *   - 'drawer'           → tente d'ouvrir le tiroir panier du thème
     *   - 'redirect'         → redirige vers /cart
     */
    /**
     * @param {string} [effectiveCode] - code promo déjà activé (pack ou unite)
     */
    _handlePostAddAction(effectiveCode = '') {
      const action = this.cfg.postAddAction || 'success';

      if (action === 'redirect') {
        // Utilise le code EFFECTIVEMENT appliqué (pack ou unite selon l'ajout).
        // Garantit que la remise est active avant /cart, même en cas de race
        // condition dans le fetch async de _applyDiscountCode.
        window.location.href = effectiveCode
          ? `/discount/${encodeURIComponent(effectiveCode)}?redirect=/cart`
          : '/cart';
        return;
      }

      if (action === 'drawer') {
        const opened = openThemeCartDrawer();
        if (opened) {
          // Drawer ouvert : toast de confirmation avec le titre configuré
          const title = this.cfg.successTitle || 'Ajouté à votre panier !';
          this._showToast(`✓ ${title}`, 4000);
          return;
        }
        // Fallback : si aucun drawer détecté, écran succès
      }

      // Default
      this._showSuccess();
    }

    /**
     * Émet les événements analytics standards :
     *   - dataLayer.push pour GA4 / GTM
     *   - CustomEvent DOM pour intégration thème custom
     */
    _dispatchAnalytics(eventName, payload) {
      // Calcul du total pour l'event
      let totalCents = 0;
      const eventItems = (payload.items || []).map((it) => {
        const variant = this._findVariant(it.id);
        const prod    = this._productIdForVariant(it.id) != null
          ? this.cfg.products.find((p) => p.variants.some((v) => v.id === it.id))
          : null;
        const { discounted } = computePrice(
          variant?.price,
          variant?.compareAtPrice,
          this._effectiveDiscount(prod) // bug fix: discountPercentUnite pour unite
        );
        totalCents += discounted;
        return {
          item_id:    String(it.id),
          item_name:  prod?.title || '',
          item_brand: this.cfg.shopBrand || '',
          item_variant: variant?.title || '',
          price:      discounted / 100,
          quantity:   it.quantity || 1,
        };
      });

      const detail = {
        event_name:  eventName,
        section_id:  this.sid,
        currency:    'EUR',
        value:       totalCents / 100,
        items:       eventItems,
      };

      // 1. dataLayer (GA4 / GTM)
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: `packdecouverteverlou_${eventName}`,
          ecommerce: {
            currency: 'EUR',
            value:    totalCents / 100,
            items:    eventItems,
          },
        });
      } catch (_) {}

      // 2. CustomEvent DOM pour intégration thème
      try {
        document.dispatchEvent(
          new CustomEvent(`packdecouverteverlou:${eventName}`, { detail })
        );
      } catch (_) {}
    }

    /**
     * Retourne le taux de remise effectif pour un produit.
     *
     * Priorité :
     *   1) prod.discountPercent > 0  → remise explicite du bloc (déjà résolue
     *      par le Liquid selon le type, mais on garde en double protection)
     *   2) packType === 'unite'       → discountPercentUnite (50% par défaut)
     *   3) sinon                      → discountPercent global packs (80%)
     *
     * Centralise la logique pour éviter les incohérences entre
     * _calcSelectionTotal, _dispatchAnalytics, _onUnitVariantChange, _reset…
     *
     * @param {object|null} prod - objet produit de cfg.products
     * @returns {number}
     */
    _effectiveDiscount(prod) {
      if (!prod) return this.cfg.discountPercent;
      if (prod.discountPercent > 0) return prod.discountPercent;
      return prod.packType === 'unite'
        ? (this.cfg.discountPercentUnite || this.cfg.discountPercent)
        : this.cfg.discountPercent;
    }

    /** Retourne l'objet variant complet depuis sa cfg, ou null. */
    _findVariant(variantId) {
      for (const prod of this.cfg.products) {
        const v = prod.variants.find((vv) => vv.id === variantId);
        if (v) return v;
      }
      return null;
    }

    async _fetchCart() {
      const resp = await this._fetchWithTimeout('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) throw new Error('Impossible de récupérer le panier');
      return resp.json();
    }

    /**
     * Supprime des line-items spécifiques du panier via /cart/change.js.
     *
     * On utilise /cart/change.js (et non /cart/update.js) car :
     *   - /cart/update.js n'accepte que des variant IDs ou des indices de
     *     position (1-indexés) comme clés → il ignore silencieusement les
     *     line-item keys hash (format "variantId:abc123").
     *   - /cart/change.js accepte la key de ligne et supprime précisément
     *     cet article sans toucher d'autres lignes du même variant.
     *
     * Les suppressions sont séquentielles pour éviter les conflits de
     * concurrence côté Shopify sur le même panier.
     *
     * @param {Set<string>} lineKeys - Set de line-item keys à mettre à qty 0
     */
    async _cartRemoveItems(lineKeys) {
      for (const key of lineKeys) {
        const resp = await this._fetchWithTimeout('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        if (!resp.ok) {
          throw new Error(
            `Impossible de mettre à jour le panier (${resp.status}). Veuillez réessayer.`
          );
        }
        await resp.json();
      }
    }

    /* ======================================================
       CART — Cadeaux offerts (ajout / retrait automatique)
       ======================================================
       Règle : dès qu'au moins un produit "à l'unité" (_pack_type=unite) est
       présent dans le panier, on ajoute les produits cadeaux configurés,
       marqués _pack_gift=true. La Shopify Function leur applique -100% (0 €).
       S'il n'y a plus aucun produit à l'unité, on les retire.

       ⚠️ Cette réconciliation ne tourne que sur la page Pack Découverte (là où
       ce module est chargé), pas sur la page /cart native. Elle s'exécute au
       chargement de la page et après chaque ajout au panier via le module. */
    async _syncGifts() {
      const gifts = this.cfg.giftItems || [];
      if (!gifts.length) return;

      try {
        const cart = await this._fetchCart();
        const hasUnit = cart.items.some(
          (ci) => ci.properties && ci.properties._pack_type === 'unite'
        );
        const giftLines = cart.items.filter(
          (ci) => ci.properties && ci.properties._pack_gift === 'true'
        );

        if (hasUnit) {
          // Ajouter les cadeaux manquants (1 exemplaire de chaque variante).
          const present = new Set(giftLines.map((ci) => ci.variant_id));
          const toAdd = gifts
            .filter((g) => g.id && !present.has(g.id))
            .map((g) => ({
              id: g.id,
              quantity: 1,
              properties: {
                Cadeau: 'Offert',          // visible au checkout / emails
                _pack_gift: 'true',        // marqueur lu par la fonction (-100%)
                _source: 'packdecouverteverlou',
              },
            }));
          if (toAdd.length) {
            await this._fetchWithTimeout('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: toAdd }),
            }).catch(() => { /* ex : cadeau en rupture de stock → non-bloquant */ });
          }
        } else if (giftLines.length) {
          // Plus aucun produit à l'unité → retirer les cadeaux.
          await this._cartRemoveItems(new Set(giftLines.map((ci) => ci.key)));
        }
      } catch (e) {
        /* réconciliation des cadeaux non-bloquante */
      }
    }

    /**
     * fetch() avec timeout via AbortController.
     * Si aucune réponse n'arrive dans `ms` millisecondes, la requête est
     * annulée et une AbortError est lancée → catchée par _performCartAdd
     * → _setLoading(false) est TOUJOURS appelé, le spinner ne reste jamais bloqué.
     *
     * @param {string}  url
     * @param {object}  [options] - options fetch standard
     * @param {number}  [ms=12000] - délai max en ms
     */
    _fetchWithTimeout(url, options = {}, ms = 12000) {
      const ctrl = new AbortController();
      const tid  = setTimeout(() => ctrl.abort(), ms);
      return fetch(url, { ...options, signal: ctrl.signal })
        .finally(() => clearTimeout(tid));
    }

    /**
     * Retrouve le productId (tel que déclaré dans la config Liquid)
     * à partir d'un variantId.
     */
    _productIdForVariant(variantId) {
      for (const prod of this.cfg.products) {
        if (prod.variants.some((v) => v.id === variantId)) {
          return prod.productId;
        }
      }
      return null;
    }

    /**
     * Tente de mettre à jour le compteur du panier dans la navigation du thème.
     * Fonctionne avec la plupart des thèmes Shopify (Dawn, etc.).
     */
    _refreshCartCount() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          // Sélecteurs courants des thèmes Shopify
          const selectors = [
            '.cart-count',
            '[data-cart-count]',
            '[data-header-cart-quantity]',
            '.header__cart-count',
            '#cart-icon-bubble .cart-count-bubble',
          ];
          selectors.forEach((sel) => {
            document.querySelectorAll(sel).forEach((el) => {
              el.textContent = cart.item_count;
            });
          });
          // Événement custom pour les thèmes qui l'écoutent
          document.dispatchEvent(
            new CustomEvent('cart:updated', { detail: { cart } })
          );
        })
        .catch(() => {});
    }

    /* ======================================================
       SUCCESS & RESET
       ====================================================== */

    _showSuccess() {
      // Cache les étapes et le stepper
      this.root
        .querySelectorAll('.pd-step, .pd-stepper, .pd-parts-divider')
        .forEach((el) => (el.style.display = 'none'));

      // Cache la barre récap
      const bar = document.getElementById(`pd-summary-${this.sid}`);
      if (bar) bar.classList.remove('pd-summary-bar--visible');

      // Affiche le succès avec animation (la classe déclenche l'animation,
      // pas le hidden=false seul — évite l'animation au chargement de page)
      const successEl = document.getElementById(`pd-success-${this.sid}`);
      if (successEl) {
        successEl.hidden = false;
        // Force reflow avant d'ajouter la classe pour que l'animation joue
        void successEl.offsetWidth;
        successEl.classList.add('pd-success--visible');
      }

      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    _reset() {
      this.unitSelections.clear();

      // Réaffiche les étapes
      this.root
        .querySelectorAll('.pd-step, .pd-stepper, .pd-parts-divider')
        .forEach((el) => (el.style.display = ''));

      // Cache le succès et retire la classe d'animation pour qu'elle puisse
      // rejouer lors de la prochaine commande
      const successEl = document.getElementById(`pd-success-${this.sid}`);
      if (successEl) {
        successEl.hidden = true;
        successEl.classList.remove('pd-success--visible');
      }

      // Remet à zéro les cards produits
      this.root
        .querySelectorAll('.pd-product-card')
        .forEach((card) => {
          card.classList.remove('pd-product-card--selected');
          card.setAttribute('aria-pressed', 'false');
        });

      // Reset pills sur la variante recommandée (sinon la première disponible)
      this.root.querySelectorAll('.pd-variant-pills').forEach((container) => {
        const productId = parseInt(container.dataset.productId, 10);
        const prod      = this.cfg.products.find((p) => p.productId === productId);
        if (!prod) return;

        const allVariants = prod.variants;
        const recIdx      = this._recommendedVariantIndex(prod, allVariants);
        const activeIdx   = allVariants[recIdx]?.available
          ? recIdx
          : allVariants.findIndex((v) => v.available);
        const pills       = container.querySelectorAll('.pd-variant-pill');

        pills.forEach((p, i) => {
          p.classList.toggle('pd-variant-pill--active', i === activeIdx);
          p.setAttribute('aria-pressed', i === activeIdx ? 'true' : 'false');
        });

        const recPill = pills[activeIdx];
        if (recPill) {
          this._updateProductCardPrice(
            productId,
            {
              price: parseInt(recPill.dataset.price, 10),
              compareAtPrice: parseInt(recPill.dataset.compare || 0, 10),
            },
            this._effectiveDiscount(prod)
          );
        }
      });

      this._updateSelectionSummary();
      this._goToStep(1);
    }

    /* ======================================================
       UTILS
       ====================================================== */

    /* ======================================================
       TOAST — Notifications d'erreur inline
       Remplace alert() : non-bloquant, stylé, auto-disparition
       ====================================================== */

    _showToast(message, duration = 6000) {
      const toast = document.getElementById(`pd-toast-${this.sid}`);
      if (!toast) return;

      const msgEl = toast.querySelector('.pd-toast__message');
      if (msgEl) msgEl.textContent = message;

      toast.hidden = false;
      // Force reflow pour que la transition CSS joue
      void toast.offsetWidth;
      toast.classList.add('pd-toast--visible');

      // Auto-disparition
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => this._hideToast(), duration);
    }

    _hideToast() {
      const toast = document.getElementById(`pd-toast-${this.sid}`);
      if (!toast) return;
      toast.classList.remove('pd-toast--visible');
      // Cache après la transition CSS (300ms)
      setTimeout(() => { toast.hidden = true; }, 300);
    }

    _setLoading(state) {
      this.isLoading = state;

      const loader = document.getElementById(`pd-loading-${this.sid}`);
      if (loader) {
        loader.hidden = !state;
        loader.setAttribute('aria-hidden', String(!state));
      }

      // Boutons "Choisir ce pack" : désactivés pendant le chargement
      this.root
        .querySelectorAll('[data-action="add-pack"]')
        .forEach((btn) => {
          btn.disabled = state;
        });

      // Bouton "Ajouter au panier" (étape 2) : désactivé pendant le
      // chargement, mais à la fin on restaure l'état correct basé sur la
      // sélection — pour ne pas ré-activer le bouton si aucun produit n'est
      // sélectionné (ex. après une erreur panier).
      const addBtn = document.getElementById(`pd-add-btn-${this.sid}`);
      if (addBtn) {
        addBtn.disabled = state || this.unitSelections.size === 0;
      }
    }

    /** Échappe les caractères HTML dangereux */
    _esc(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;'); // ajout pour attributs en simple-quote
    }

    /**
     * Cleanup : appelé quand l'éditeur de thème Shopify recharge la section.
     * Nettoie les timers en attente pour éviter les fuites mémoire et les
     * accès à des éléments DOM qui n'existent plus.
     */
    destroy() {
      if (this._toastTimer) {
        clearTimeout(this._toastTimer);
        this._toastTimer = null;
      }
      // Les listeners sur this.root sont auto-nettoyés quand le DOM est
      // remplacé par Shopify lors d'un rechargement de section.
      this.unitSelections.clear();
    }
  }

  /* ── Initialisation ─────────────────────────────────── */

  window._pdInstances = window._pdInstances || {};

  function initAll() {
    if (!window.packDecouverte) return;
    Object.keys(window.packDecouverte).forEach((sid) => {
      if (!window._pdInstances[sid]) {
        const instance = new PackDecouverte(sid);
        window._pdInstances[sid] = instance;
      }
    });
  }

  // Lancement au chargement du DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Support de l'éditeur de thème Shopify (rechargement de section)
  document.addEventListener('shopify:section:load', (e) => {
    const sid = e.detail?.sectionId;
    if (sid && window.packDecouverte?.[sid]) {
      // Cleanup propre de l'instance précédente (timers, références)
      const previous = window._pdInstances[sid];
      if (previous && typeof previous.destroy === 'function') {
        previous.destroy();
      }
      delete window._pdInstances[sid];
      window._pdInstances[sid] = new PackDecouverte(sid);
    }
  });

  // Cleanup quand une section est totalement supprimée dans l'éditeur
  document.addEventListener('shopify:section:unload', (e) => {
    const sid = e.detail?.sectionId;
    if (sid && window._pdInstances[sid]) {
      const inst = window._pdInstances[sid];
      if (inst && typeof inst.destroy === 'function') inst.destroy();
      delete window._pdInstances[sid];
      delete window.packDecouverte?.[sid];
    }
  });

})();